<script lang="ts" module>
	// The names, in the design's groups. A new name needs a real screen that
	// uses it, as the design says.
	export type IconName =
		// chat and marks
		| 'back'
		| 'more'
		| 'phone'
		| 'attach'
		| 'mic'
		| 'send'
		| 'check'
		| 'checks'
		| 'clock'
		// the back-office rail
		| 'today'
		| 'receipt'
		| 'chart'
		| 'tag'
		| 'user'
		// the manager role, beside a word and never alone
		| 'key'
		| 'store'
		| 'device'
		| 'settings'
		| 'book'
		| 'external'
		// the till
		| 'percent'
		| 'pin'
		| 'bag'
		| 'printer'
		| 'drawer'
		| 'lock'
		| 'pause'
		// video (D22 Help): play points right in both languages and never
		// mirrors, because it is a media convention, not a reading direction
		| 'play'
		| 'fullscreen'
		| 'cash'
		| 'card'
		| 'offline'
		// universal controls, which may stand alone with a label
		| 'backspace'
		| 'close'
		| 'plus'
		| 'minus'
		| 'search'
		| 'expand'
		// move one place in an ordered list, beside the grip; stands alone, with a label
		| 'up'
		| 'down'
		// a drag handle on a reorderable row; stands alone, with a label naming the row
		| 'grip'
		| 'edit'
		// a receipt section's controls (D11 Printing, #637): each an icon-only
		// toggle that stands alone, with a label naming the action and the row
		| 'center'
		| 'visible'
		| 'hidden'
		| 'bold'
		| 'dashed'
		| 'gap'
		| 'bin'
		| 'oneline';
</script>

<script lang="ts">
	// The design system's Icon (design/components/core/Icon.jsx): the product
	// set, for the back-office and the till and never the public site.
	//
	// An icon sits beside a word and takes its colour (currentColor), so it
	// follows the press, the disabled look and the emphasis surface with no
	// rule of its own. Only backspace, close, back, plus, minus, search, up,
	// down and grip stand alone, and then with a label; so do a receipt
	// section's icon-only toggles (center, visible, hidden, bold, dashed,
	// gap, bin, oneline), each with a label naming the action and the row and
	// pressed while on (#637). A 24-unit box, a 1.8 stroke, round caps and
	// joins, no fills but the dots of `more` and `grip`; `bold` alone is drawn
	// with a 3.2 stroke, so it looks like what it means. Sizes by job: 18 in a
	// nav row, 20 in a small button, 22 in a large one, 28 on a key.
	//
	// back and send are drawn for Arabic; mirror them on an English page.
	// backspace never mirrors, because the keypad it lives on never does.

	interface Props {
		name: IconName;
		size?: number;
		mirror?: boolean;
		/** Makes the icon speak. Without it the icon is hidden from readers. */
		label?: string;
	}

	let { name, size = 20, mirror = false, label }: Props = $props();
</script>

<svg
	class:mirror
	viewBox="0 0 24 24"
	width={size}
	height={size}
	fill="none"
	stroke="currentColor"
	stroke-width="1.8"
	stroke-linecap="round"
	stroke-linejoin="round"
	role={label ? 'img' : undefined}
	aria-label={label}
	aria-hidden={label ? undefined : 'true'}
>
	{#if name === 'back'}
		<path d="M9 5l7 7-7 7" />
	{:else if name === 'more'}
		<g fill="currentColor" stroke="none">
			<circle cx="12" cy="5.5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle
				cx="12"
				cy="18.5"
				r="1.6"
			/>
		</g>
	{:else if name === 'phone'}
		<path
			d="M6.5 3.5h3l1.8 4.6-2.2 1.4a11 11 0 0 0 5.4 5.4l1.4-2.2 4.6 1.8v3a2 2 0 0 1-2.1 2A16.5 16.5 0 0 1 4.5 5.6a2 2 0 0 1 2-2.1z"
		/>
	{:else if name === 'attach'}
		<path
			d="M20 11.5l-8.2 8.2a5 5 0 0 1-7.1-7.1L13 4.3a3.4 3.4 0 0 1 4.8 4.8l-8.3 8.3a1.8 1.8 0 0 1-2.5-2.5L14.7 7"
		/>
	{:else if name === 'mic'}
		<rect x="9" y="3" width="6" height="11" rx="3" /><path
			d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21"
		/>
	{:else if name === 'send'}
		<path d="M21 12L3.5 4.5 7 12l-3.5 7.5zM7 12h6" />
	{:else if name === 'check'}
		<path d="M5 12.5l4.5 4.5L19 7.5" />
	{:else if name === 'checks'}
		<path d="M2.5 12.5L7 17l9.5-9.5M11.5 16l1 1L22 7.5" />
	{:else if name === 'clock'}
		<circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" />
	{:else if name === 'today'}
		<path d="M3 12h4l2.5-6 5 12 2.5-6H21" />
	{:else if name === 'receipt'}
		<path d="M6 3h12v18l-2-1.4-2 1.4-2-1.4-2 1.4-2-1.4-2 1.4z" /><path d="M9 8h6M9 12h6M9 16h3" />
	{:else if name === 'chart'}
		<path d="M4 20h16M7 16v-5M12 16V6M17 16V9" />
	{:else if name === 'tag'}
		<path
			d="M3.5 12.2V4.5a1 1 0 0 1 1-1h7.7l8.3 8.3a1.5 1.5 0 0 1 0 2.1l-6.2 6.2a1.5 1.5 0 0 1-2.1 0z"
		/><circle cx="8" cy="8" r="1.5" />
	{:else if name === 'user'}
		<circle cx="12" cy="8" r="3.5" /><path d="M5 20a7 7 0 0 1 14 0" />
	{:else if name === 'key'}
		<circle cx="8" cy="15" r="4" /><path d="M11 12l8.5-8.5M16 7l2.5 2.5M14 9l2 2" />
	{:else if name === 'store'}
		<path d="M4 9.5L5.5 4h13L20 9.5" /><path
			d="M4 9.5a2.7 2.7 0 0 0 5.3 0 2.7 2.7 0 0 0 5.4 0 2.7 2.7 0 0 0 5.3 0"
		/><path d="M5.5 12v8h13v-8M10 20v-4.5h4V20" />
	{:else if name === 'device'}
		<rect x="3" y="4" width="18" height="12" rx="1.5" /><path d="M9 20h6M12 16v4" />
	{:else if name === 'settings'}
		<path d="M4 7h9M17 7h3M4 17h3M11 17h9" /><circle cx="15" cy="7" r="2" /><circle
			cx="9"
			cy="17"
			r="2"
		/>
	{:else if name === 'book'}
		<!-- The catalogue (D16 rail entry): an open book, the restaurant's page in a list of pages. -->
		<path
			d="M12 6.5C10 5 7 4.5 3.5 5v13c3.5-.5 6.5 0 8.5 1.5 2-1.5 5-2 8.5-1.5V5C17 4.5 14 5 12 6.5z"
		/><path d="M12 6.5v13" />
	{:else if name === 'external'}
		<!-- Leaves this app for another site. Beside the word, after it. -->
		<path d="M14 4.5h5.5V10M19.5 4.5L11 13" /><path
			d="M17 13.5v5a1 1 0 0 1-1 1H5.5a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h5"
		/>
	{:else if name === 'percent'}
		<path d="M18 6L6 18" /><circle cx="7.5" cy="7.5" r="2" /><circle cx="16.5" cy="16.5" r="2" />
	{:else if name === 'pin'}
		<path d="M12 21s6.5-6 6.5-11.2a6.5 6.5 0 0 0-13 0C5.5 15 12 21 12 21z" /><circle
			cx="12"
			cy="9.8"
			r="2.3"
		/>
	{:else if name === 'bag'}
		<path d="M5.5 8h13l-1 12.5h-11z" /><path d="M9 8V6.5a3 3 0 0 1 6 0V8" />
	{:else if name === 'printer'}
		<path d="M7 9V3.5h10V9" /><rect x="3.5" y="9" width="17" height="7.5" rx="1.5" /><path
			d="M7 14h10v6.5H7z"
		/>
	{:else if name === 'drawer'}
		<path d="M7 10V5.5h10V10" /><rect x="3" y="10" width="18" height="9" rx="1.5" /><path
			d="M3 14h18M10 16.5h4"
		/>
	{:else if name === 'lock'}
		<rect x="5" y="10.5" width="14" height="10" rx="1.8" /><path
			d="M8 10.5V8a4 4 0 0 1 8 0v2.5M12 14.5V17"
		/>
	{:else if name === 'pause'}
		<path d="M9 5v14M15 5v14" />
	{:else if name === 'play'}
		<path d="M7 4.5v15l12-7.5z" />
	{:else if name === 'fullscreen'}
		<path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
	{:else if name === 'cash'}
		<rect x="2.5" y="6.5" width="19" height="11" rx="1.5" /><circle cx="12" cy="12" r="2.6" /><path
			d="M6 9.5v5M18 9.5v5"
		/>
	{:else if name === 'card'}
		<rect x="2.5" y="5.5" width="19" height="13" rx="1.8" /><path d="M2.5 10h19M6 15h4" />
	{:else if name === 'offline'}
		<path
			d="M2.5 9a14 14 0 0 1 19 0M5.5 12.5a9.5 9.5 0 0 1 13 0M8.8 16a4.8 4.8 0 0 1 6.4 0M12 19.5h.01M4 4l16 16"
		/>
	{:else if name === 'backspace'}
		<path d="M9 5h10.5A1.5 1.5 0 0 1 21 6.5v11a1.5 1.5 0 0 1-1.5 1.5H9l-6-7z" /><path
			d="M12.5 9.5l5 5M17.5 9.5l-5 5"
		/>
	{:else if name === 'close'}
		<path d="M6 6l12 12M18 6L6 18" />
	{:else if name === 'plus'}
		<path d="M12 5v14M5 12h14" />
	{:else if name === 'minus'}
		<path d="M5 12h14" />
	{:else if name === 'search'}
		<circle cx="10.5" cy="10.5" r="6" /><path d="M15 15l5 5" />
	{:else if name === 'expand'}
		<path d="M6 9.5l6 6 6-6" />
	{:else if name === 'up'}
		<path d="M6 14.5l6-6 6 6" />
	{:else if name === 'down'}
		<path d="M6 9.5l6 6 6-6" />
	{:else if name === 'grip'}
		<g fill="currentColor" stroke="none">
			<circle cx="9" cy="6" r="1.5" /><circle cx="15" cy="6" r="1.5" /><circle
				cx="9"
				cy="12"
				r="1.5"
			/><circle cx="15" cy="12" r="1.5" /><circle cx="9" cy="18" r="1.5" /><circle
				cx="15"
				cy="18"
				r="1.5"
			/>
		</g>
	{:else if name === 'edit'}
		<path d="M4 20l1-4L16 5a2.1 2.1 0 0 1 3 3L8 19z" /><path d="M14 7l3 3" />
	{:else if name === 'center'}
		<path d="M5 6h14M8 10h8M5 14h14M8 18h8" />
	{:else if name === 'visible'}
		<path d="M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12z" /><circle
			cx="12"
			cy="12"
			r="2.8"
		/>
	{:else if name === 'hidden'}
		<path d="M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12z" /><circle
			cx="12"
			cy="12"
			r="2.8"
		/><path d="M4 4l16 16" />
	{:else if name === 'bold'}
		<!-- The one icon with a heavier stroke, so it looks bold itself. -->
		<path stroke-width="3.2" d="M8 5h5a3.5 3.5 0 0 1 0 7H8zM8 12h5.8a3.5 3.5 0 0 1 0 7H8z" />
	{:else if name === 'dashed'}
		<path d="M3 12h3M10.5 12h3M18 12h3" />
	{:else if name === 'gap'}
		<path d="M4 5.5h16M4 18.5h16M12 9v6M9.8 10.8L12 8.6l2.2 2.2M9.8 13.2l2.2 2.2 2.2-2.2" />
	{:else if name === 'bin'}
		<path d="M4.5 7h15M9.5 7V4.5h5V7M6.5 7l1 12.5h9l1-12.5M10 11v5M14 11v5" />
	{:else if name === 'oneline'}
		<path d="M4 8.5h6.5M13.5 8.5H20M4 15.5h16" />
	{/if}
</svg>

<style>
	svg {
		display: block;
		flex: none;
	}

	.mirror {
		transform: scaleX(-1);
	}
</style>
